import { memo, useCallback } from "react"
import { useSelector } from "react-redux"
import AlertWearable from "@/components/AlertWearable"
import WearableCard from "@/components/WearableCard"
import { RootState } from "@/store"
import { selectWearableRealtimeById } from "@/store/wearablesRealtimeSlice"
import { Alert, AlertVariant, TypefaceVariant } from "@/types/alert"
import { getHighestPrioAlert } from "@/utils/cells"
import { formatInmateName } from "@/utils/index"
import { get } from "@/utils/lodash"

interface WearableCardRendererProps {
   location?: string
   wearableId: string
   alertsByInmateId: Record<string, Alert[]>
   onSwapDeviceClick: (wearableId: string) => void
   onClearAlertClick: (alert: Alert) => void
   onNavigateToInmateDetail: (inmateId: string) => void
   isExpanded?: boolean
   isCollapsed?: boolean
   onExpandedChange?: (wearableId: string, isExpanded: boolean) => void
}

const WearableCardWrapper = ({
   location,
   wearableId,
   alertsByInmateId,
   onSwapDeviceClick,
   onClearAlertClick,
   onNavigateToInmateDetail,
   isExpanded,
   isCollapsed,
   onExpandedChange,
}: WearableCardRendererProps) => {
   const wearable = useSelector((state: RootState) =>
      selectWearableRealtimeById(state, wearableId)
   )

   const handleSwapDevice = useCallback(() => {
      if (wearable?.id) {
         onSwapDeviceClick(wearable.id)
      }
   }, [onSwapDeviceClick, wearable?.id])

   const handleViewDetails = useCallback(() => {
      if (wearable?.inmate?.id) {
         onNavigateToInmateDetail(wearable.inmate.id)
      }
   }, [onNavigateToInmateDetail, wearable?.inmate?.id])

   if (!wearable) {
      return null
   }

   const inmateName =
      wearable.inmate?.customIdentifier || formatInmateName(wearable.inmate)
   const bookingNumber = wearable.inmate?.bookingNumber

   const inmateAlerts = wearable.inmate?.id
      ? alertsByInmateId[wearable.inmate.id]
      : undefined

   const hasActiveAlert = inmateAlerts && inmateAlerts.length > 0
   if (hasActiveAlert) {
      const alert = getHighestPrioAlert(inmateAlerts)

      if (!alert) return null

      const alertVariant = get(
         alert,
         "alertConfiguration.color.card",
         ""
      ).toLowerCase() as keyof typeof AlertVariant
      const typefaceColor = get(
         alert,
         "alertConfiguration.color.typeface",
         ""
      ).toLowerCase() as keyof typeof TypefaceVariant

      return (
         <AlertWearable
            inmateName={inmateName}
            bookingNumber={bookingNumber}
            chargeLevel={wearable.vitals?.battery}
            {...(location && {
               location,
            })}
            systemConditionType={alert.alertConfiguration.systemCondition?.type}
            alertVariant={alertVariant}
            typefaceColor={typefaceColor}
            vitals={alert.vitals}
            onSwapDevice={handleSwapDevice}
            onClearAlert={() => alert && onClearAlertClick(alert)}
            onViewDetails={handleViewDetails}
         />
      )
   }
   return (
      <WearableCard
         wearableId={wearable.id}
         inmateName={inmateName}
         bookingNumber={bookingNumber}
         bpm={wearable.vitals?.heartRate ?? undefined}
         oxygenLevel={wearable.vitals?.oxygenLevel ?? undefined}
         awaitingVitals={wearable.vitals === null}
         chargeLevel={wearable.vitals?.battery}
         {...(location && {
            location,
         })}
         onSwapDevice={handleSwapDevice}
         onViewDetails={handleViewDetails}
         isExpanded={isExpanded}
         isCollapsed={isCollapsed}
         onExpandedChange={(nextIsExpanded) =>
            onExpandedChange?.(wearable.id, nextIsExpanded)
         }
      />
   )
}

export default memo(WearableCardWrapper)
